import React from "react";
import { Download, Share2, X } from "lucide-react";
import { useLang } from "@/lib/LanguageContext";
import {
  clearDeferredInstallPrompt,
  getDeferredInstallPrompt,
  isIosDevice,
  isMobileDevice,
  isStandalonePwa,
  PWA_APP_INSTALLED_EVENT,
  PWA_INSTALL_PROMPT_READY_EVENT,
} from "@/lib/pwa";
import { cn } from "@/lib/utils";

const STORAGE_KEY = "aevo_install_prompt_dismissed_v2";
const HIDDEN_PATHS = ["/admin", "/auth", "/checkout", "/join", "/support", "/status"];

const COPY = {
  en: {
    title: "Install Aevo",
    body: "Open galleries like a mobile app, directly from your home screen.",
    install: "Install",
    ios: "On iPhone: tap Share, then Add to Home Screen.",
    iosSteps: "Safari only: Share icon -> Add to Home Screen -> Add.",
    later: "Later",
    understood: "Got it",
  },
  fr: {
    title: "Installer Aevo",
    body: "Ouvre tes galeries comme une app mobile, directement depuis ton ecran d'accueil.",
    install: "Installer",
    ios: "Sur iPhone: appuie sur Partager, puis Ajouter a l'ecran d'accueil.",
    iosSteps: "Depuis Safari: icone Partager -> Ajouter a l'ecran d'accueil -> Ajouter.",
    later: "Plus tard",
    understood: "Compris",
  },
  es: {
    title: "Instalar Aevo",
    body: "Abre tus galerias como una app movil desde la pantalla de inicio.",
    install: "Instalar",
    ios: "En iPhone: toca Compartir y luego Agregar a inicio.",
    iosSteps: "Desde Safari: Compartir -> Agregar a inicio -> Agregar.",
    later: "Luego",
    understood: "Entendido",
  },
  ar: {
    title: "تثبيت Aevo",
    body: "افتح معارضك كتطبيق من الشاشة الرئيسية.",
    install: "تثبيت",
    ios: "على iPhone: اضغط مشاركة ثم إضافة إلى الشاشة الرئيسية.",
    iosSteps: "من Safari: مشاركة -> إضافة إلى الشاشة الرئيسية -> إضافة.",
    later: "لاحقا",
    understood: "فهمت",
  },
};

function shouldHideOnPath() {
  if (typeof window === "undefined") return true;
  return HIDDEN_PATHS.some((path) => window.location.pathname.startsWith(path));
}

export default function AppInstallPrompt() {
  const { lang, isRTL } = useLang();
  const copy = COPY[lang] || COPY.en;
  const [deferredPrompt, setDeferredPrompt] = React.useState(null);
  const [visible, setVisible] = React.useState(false);
  const ios = isIosDevice();

  React.useEffect(() => {
    if (!isMobileDevice() || isStandalonePwa() || shouldHideOnPath()) return;
    if (localStorage.getItem(STORAGE_KEY) === "true") return;

    let canShowPrompt = false;

    const syncPromptState = () => {
      const prompt = getDeferredInstallPrompt();
      setDeferredPrompt(prompt);
      if (canShowPrompt && (prompt || ios)) setVisible(true);
    };

    const onInstalled = () => {
      localStorage.setItem(STORAGE_KEY, "true");
      setVisible(false);
      setDeferredPrompt(null);
      clearDeferredInstallPrompt();
    };

    const showTimer = window.setTimeout(() => {
      canShowPrompt = true;
      syncPromptState();
    }, ios ? 9000 : 7000);
    window.addEventListener(PWA_INSTALL_PROMPT_READY_EVENT, syncPromptState);
    window.addEventListener(PWA_APP_INSTALLED_EVENT, onInstalled);
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.clearTimeout(showTimer);
      window.removeEventListener(PWA_INSTALL_PROMPT_READY_EVENT, syncPromptState);
      window.removeEventListener(PWA_APP_INSTALLED_EVENT, onInstalled);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [ios]);

  if (!visible) return null;

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, "true");
    setVisible(false);
  };

  const install = async () => {
    const prompt = deferredPrompt || getDeferredInstallPrompt();
    if (!prompt) return;
    await prompt.prompt();
    await prompt.userChoice.catch(() => null);
    clearDeferredInstallPrompt();
    setDeferredPrompt(null);
    dismiss();
  };

  return (
    <div
      className={cn(
        "fixed inset-x-3 bottom-[calc(1rem+env(safe-area-inset-bottom,0px))] z-[70] mx-auto max-w-md rounded-xl border border-slate-200 bg-white p-4 text-slate-950 shadow-2xl shadow-slate-950/20 ring-1 ring-slate-950/5 backdrop-blur-xl",
        isRTL ? "text-right" : "text-left",
      )}
      dir={isRTL ? "rtl" : "ltr"}
    >
      <button
        type="button"
        onClick={dismiss}
        className={cn(
          "absolute top-3 flex h-11 w-11 items-center justify-center rounded-full bg-slate-100 text-slate-600 transition-colors hover:bg-slate-200 hover:text-slate-950",
          isRTL ? "left-3" : "right-3",
        )}
        aria-label={copy.later}
      >
        <X className="h-4 w-4" />
      </button>
      <div className={cn("flex items-start gap-3", isRTL ? "pl-9" : "pr-9")}>
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-1 ring-primary/15">
          {ios ? <Share2 className="h-5 w-5" /> : <Download className="h-5 w-5" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-slate-950">{copy.title}</p>
          <p className="mt-1 text-xs font-medium leading-relaxed text-slate-700">
            {ios ? copy.ios : copy.body}
          </p>
          {ios && (
            <p className="mt-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] font-semibold leading-relaxed text-slate-800">
              {copy.iosSteps || copy.ios}
            </p>
          )}
          {!ios && deferredPrompt && (
            <button
              type="button"
              onClick={install}
              className="mt-3 inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-primary px-4 text-xs font-bold text-primary-foreground shadow-brand-sm transition-transform active:scale-[0.98]"
            >
              <Download className="h-4 w-4" />
              {copy.install}
            </button>
          )}
          {ios && (
            <button
              type="button"
              onClick={dismiss}
              className="mt-3 inline-flex h-11 items-center justify-center rounded-xl bg-primary px-4 text-xs font-bold text-primary-foreground shadow-brand-sm transition-transform active:scale-[0.98]"
            >
              {copy.understood || copy.later}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
